/**
 * @file        src/layout/AppLayout.jsx
 * @description Top-level layout for Beyond Destination. The Neon left sidebar is
 *              replaced by the demo's top navigation (see TopNav.jsx), mounted here
 *              ONCE for every route: transparent over the gradient on Home,
 *              a sticky solid dark bar everywhere else. The variant is a prop, so
 *              the nav (and its logo) is never unmounted by a route change.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-08-20 Claude — WhatsApp button is now HOME-ONLY (`isHome` gate), on the
 *                          client's call: it was showing up over the flight/hotel
 *                          booking flow and Insurance, where they don't want it.
 *   - 2026-08-04 Claude — TopNav is mounted here for ALL routes instead of Home
 *                          rendering its own copy. The two mounts meant every
 *                          Home ↔ Holidays/Hotels/Flights move rebuilt the nav from
 *                          scratch — the logo <img> was recreated (visible blink),
 *                          and because Home's copy sat inside its lazy chunk the bar
 *                          disappeared entirely while that chunk loaded, which is
 *                          what read as "the whole page reloads".
 *   - 2026-07-22 Claude — Beyond Destination fork: sidebar → TopNav. Removed the
 *                          mobile hamburger / backdrop / sidebar tooltip and the
 *                          floating theme toggle (theme now lives in the nav).
 *   - 2026-07-31 Claude — Mount the floating WhatsApp contact button (bottom-right)
 *                          on every in-app page.
 *   - 2026-07-16 Claude — Mobile: expose the Light/Dark toggle as a persistent
 *                          top-right mini button.
 *   - 2026-06-22 Claude — Initial.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import TopNav from './TopNav.jsx';
import WhatsAppButton from './WhatsAppButton.jsx';

export default function AppLayout({ children }) {
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  return (
    <>
      {/* ONE TopNav for every route — the variant is a PROP, never a different
          mount. Home used to render its own hero nav from inside its page module
          while this file rendered the solid bar for everything else, so each
          Home ↔ inner-page navigation destroyed one nav and built another: the
          <img src="/logo.png"> was a brand-new DOM node every time, which is why
          the logo blinked. Worse, Home's copy lived inside its lazily-imported
          chunk, so on the way TO Home the bar vanished the moment the route
          changed and only came back once the chunk resolved — a blank strip that
          read as a full page reload.
          Rendered here, outside <Suspense>, the component instance survives every
          navigation. React only patches the header's className/style between hero
          and solid; the logo element itself is never touched, so it cannot reload
          or shift no matter how long the next page's chunk takes.
          Positioning is unchanged: the hero variant is `absolute top-0 inset-x-0`,
          it was previously anchored to Home's `relative` hero wrapper which starts
          at the document origin, and out here it resolves to the same origin. Being
          out of flow, it does not push `children` down either. */}
      <TopNav variant={isHome ? 'hero' : 'solid'} />
      {children}
      {/* Floating WhatsApp contact, bottom-right — HOME PAGE ONLY. Booking flows
          (flights, hotels, insurance) and every other inner page must not show it. */}
      {isHome && <WhatsAppButton />}
    </>
  );
}
